import React, { useState, useRef, useEffect, useCallback } from 'react';
import { BRAND } from './statuses';

interface Props {
  onSendReply: (body: string) => Promise<void>;
  onAddNote: (body: string) => Promise<void>;
  requesterEmail: string | null;
  disabledReason?: string;
}

type Tab = 'reply' | 'note';

export const ReplyComposer: React.FC<Props> = ({
  onSendReply,
  onAddNote,
  requesterEmail,
  disabledReason,
}) => {
  const [tab, setTab] = useState<Tab>('reply');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const canReply = tab === 'reply' && !!requesterEmail;

  useEffect(() => {
    setErr(null);
  }, [tab]);

  const handleSubmit = useCallback(async () => {
    if (!body.trim() || busy) return;
    setBusy(true);
    setErr(null);
    try {
      if (tab === 'reply') {
        await onSendReply(body);
      } else {
        await onAddNote(body);
      }
      setBody('');
    } catch (e: any) {
      setErr(e?.message || 'Failed to send');
    } finally {
      setBusy(false);
    }
  }, [body, busy, tab, onSendReply, onAddNote]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const submitDisabled = busy || !body.trim() || (tab === 'reply' && !canReply);

  return (
    <div
      style={{
        border: `1px solid ${BRAND.border}`,
        borderRadius: 12,
        background: BRAND.surface,
        overflow: 'hidden',
      }}
    >
      {/* Tab bar */}
      <div
        style={{
          display: 'flex',
          borderBottom: `1px solid ${BRAND.border}`,
          background: BRAND.surfaceAlt,
        }}
      >
        <TabButton
          active={tab === 'reply'}
          label="Public reply"
          hint={requesterEmail ? `to ${requesterEmail}` : 'no email on file'}
          onClick={() => setTab('reply')}
          activeColor={BRAND.green}
        />
        <TabButton
          active={tab === 'note'}
          label="Internal note"
          hint="not sent to the user"
          onClick={() => setTab('note')}
          activeColor="#B45309"
        />
      </div>

      {/* Body */}
      <div
        style={{
          padding: 16,
          background: tab === 'note' ? BRAND.noteYellow : BRAND.surface,
        }}
      >
        <textarea
          ref={textareaRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            tab === 'reply'
              ? requesterEmail
                ? 'Write a reply to the user...'
                : 'This ticket has no email on file — switch to Internal note.'
              : 'Write an internal note (only visible to admins)...'
          }
          disabled={tab === 'reply' && !canReply}
          rows={6}
          style={{
            width: '100%',
            padding: 12,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 8,
            fontSize: 14,
            fontFamily: 'inherit',
            resize: 'vertical',
            boxSizing: 'border-box',
            lineHeight: 1.6,
            color: BRAND.textSecondary,
            background: tab === 'note' ? '#FFFEF9' : BRAND.surface,
          }}
        />

        {err && (
          <div
            style={{
              marginTop: 10,
              padding: '8px 12px',
              background: '#FEE2E2',
              border: '1px solid #FCA5A5',
              borderRadius: 6,
              fontSize: 12,
              color: '#991B1B',
            }}
          >
            {err}
          </div>
        )}

        <div
          style={{
            marginTop: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ fontSize: 11, color: BRAND.textMuted }}>
            ⌘/Ctrl + Enter to send
            {disabledReason && (
              <span style={{ marginLeft: 12, color: '#B45309' }}>· {disabledReason}</span>
            )}
          </div>
          <button
            onClick={handleSubmit}
            disabled={submitDisabled}
            style={{
              padding: '9px 22px',
              borderRadius: 8,
              background: submitDisabled
                ? BRAND.surfaceMuted
                : tab === 'reply'
                ? BRAND.green
                : '#B45309',
              color: submitDisabled ? BRAND.textMuted : '#fff',
              border: 'none',
              fontSize: 13,
              fontWeight: 700,
              cursor: submitDisabled ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
              letterSpacing: '0.01em',
            }}
          >
            {busy
              ? 'Sending…'
              : tab === 'reply'
              ? 'Send reply'
              : 'Save note'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TabButton: React.FC<{
  active: boolean;
  label: string;
  hint: string;
  onClick: () => void;
  activeColor: string;
}> = ({ active, label, hint, onClick, activeColor }) => (
  <button
    onClick={onClick}
    style={{
      flex: 1,
      padding: '12px 16px',
      background: active ? BRAND.surface : 'transparent',
      border: 'none',
      borderBottom: active ? `2px solid ${activeColor}` : '2px solid transparent',
      cursor: 'pointer',
      textAlign: 'left',
      borderRadius: 0,
      transition: 'background 0.12s',
    }}
  >
    <div
      style={{
        fontSize: 13,
        fontWeight: 700,
        color: active ? activeColor : BRAND.textSecondary,
        marginBottom: 2,
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 11, color: BRAND.textMuted }}>{hint}</div>
  </button>
);
